<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import AtSign from '@lucide/vue/dist/esm/icons/at-sign.mjs'; import Bell from '@lucide/vue/dist/esm/icons/bell.mjs'; import Building2 from '@lucide/vue/dist/esm/icons/building-2.mjs'; import Check from '@lucide/vue/dist/esm/icons/check.mjs'; import Download from '@lucide/vue/dist/esm/icons/download.mjs'; import Fingerprint from '@lucide/vue/dist/esm/icons/fingerprint-pattern.mjs'; import LayoutGrid from '@lucide/vue/dist/esm/icons/layout-grid.mjs'; import LogOut from '@lucide/vue/dist/esm/icons/log-out.mjs'; import Settings from '@lucide/vue/dist/esm/icons/settings.mjs'; import Sparkles from '@lucide/vue/dist/esm/icons/sparkles.mjs'; import User from '@lucide/vue/dist/esm/icons/user.mjs';
import { ChatIcon } from '@blpde/nx-icons';
import { connectTileId } from '@blpde/connect/config';
import { LogoFormicaDark, NavBar, useNxToasts } from '@blpde/nx-ui';
import { apps, appForPath } from '@/integration';
import { appNav } from '@/integration/loader';
import type { NavItem } from '@/integration/nav';
import { signOut, useShellUser } from '@/auth';
import { useAccountApplications } from '@/applications';
import { useModuleAvailability } from '@/modules';
import { useMentionUnread } from '@/mentions';
import { useMasterNotifications } from '@/notifications';
import { companyLabel, scopeSwitchErrorKey, tenantLabel, useSessionScope } from '@/scope';
import ShellNavItem from '@/components/ShellNavItem.vue';
import ShellNotificationsPanel from '@/components/ShellNotificationsPanel.vue';
import UserAvatar from '@/components/UserAvatar.vue';
import { usePermissionStore } from '@erp/connect/shell';
import { appMessages } from '@/i18n/appI18n';
import { fromTileNavItems, useTileNavbarMode, useTileNavReport } from '@/tiles/tileNav';
import { useTileWorkspaces } from '@/tiles/useTileWorkspaces';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { user } = useShellUser();
const { isAvailable } = useModuleAvailability();
const { isEntitled } = useAccountApplications();
// A tile of a workspace (I-3641) keeps only its app's own menu: mentions, bell,
// chat, apps and account are the page around it, once.
const inTile = connectTileId() !== null;
const mentions = inTile ? null : useMentionUnread();
// The bell's count. ShellNav is not keyed, so the poller runs once for the shell.
const notifications = inTile ? null : useMasterNotifications();
const erpPermissions = usePermissionStore();
const sessionScope = useSessionScope();
const { showToast } = useNxToasts();

// A refused switch keeps the session where it was; say why.
watch(
  () => sessionScope.switchError.value,
  (code) => {
    if (code) showToast(t(scopeSwitchErrorKey(code)), 'error');
  },
);

// Active module → its navbar contribution.
const activeApp = computed(() => appForPath(route.path));
function itemAllowed(item: NavItem): boolean {
  if (!item.requiredPermissions) return true;
  return erpPermissions.can(item.requiredPermissions, item.permissionMode ?? 'all');
}

function filterPermissionedItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => {
    if (!itemAllowed(item)) return [];
    const children = item.children ? filterPermissionedItems(item.children) : undefined;
    if (item.children && children?.length === 0 && !item.to && !item.action) return [];
    return [{ ...item, ...(children ? { children } : {}) }];
  });
}

const appLeftItems = computed(() => filterPermissionedItems(appNav(activeApp.value)?.left ?? []));

// The active app lets the user drag its left entries into their own order
// (I-3239): the NavBar reports the movable entries' ids in their new order and
// the app stores it.
const reorderable = computed(() => typeof appNav(activeApp.value)?.reorder === 'function');
function onReorder(ids: string[]) {
  appNav(activeApp.value)?.reorder?.(ids);
}
const appRightItems = computed(() =>
  filterPermissionedItems(appNav(activeApp.value)?.right ?? []),
);

// Inside a tile the menu may live in the page's one navbar (`shell.tiles.navbar`).
const tileNavbarMode = inTile
  ? useTileNavReport({
      left: () => appLeftItems.value,
      right: () => appRightItems.value,
      label: (text) => {
        const app = activeApp.value?.id;
        if (!app) return text;
        const messages = appMessages(app);
        return messages.te(text) ? messages.t(text) : text;
      },
      router,
    })
  : useTileNavbarMode();
const noBarInTile = computed(() => inTile && tileNavbarMode.value === 'shared');

// On the page: the workspaces' active tile lends its menu to this navbar.
const tiles = useTileWorkspaces();
const activeTileNav = computed(() =>
  !inTile && route.path === '/tiles' && tileNavbarMode.value === 'shared'
    ? tiles.activeTileNav.value
    : null,
);
function runInActiveTile(tileId: string, itemId: string): void {
  tiles.sendToTile(tileId, { type: 'connect.tile.nav-action', itemId });
}
const leftItems = computed(() => {
  const shared = activeTileNav.value;
  return shared
    ? fromTileNavItems(shared.nav.left, (id) => runInActiveTile(shared.tileId, id))
    : appLeftItems.value;
});
const moduleRightItems = computed(() => {
  const shared = activeTileNav.value;
  return shared
    ? fromTileNavItems(shared.nav.right, (id) => runInActiveTile(shared.tileId, id))
    : appRightItems.value;
});

/** Among tabs, the logo and the app menu act on the active tile, not on the page around it. */
function go(path: string) {
  const tile = route.path === '/tiles' ? tiles.active.value?.activeTileId : null;
  if (!inTile && tile) tiles.openInTile(tile, path);
  else router.push(path);
}
function goHome() {
  go('/');
}
function goApp(app: (typeof apps)[number]) {
  // Account entitlement is hard — no grant, no jump. Module connectivity is
  // soft (app surfaces offline itself) so we still navigate when entitled.
  if (!isEntitled(app.id)) return;
  go(app.basePath);
}

const userName = computed(() => user.value?.name || user.value?.email || t('connect.menu.account'));
function countBadge(count: number): string | null {
  if (count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}
const mentionBadge = computed(() => countBadge(mentions?.unreadCount.value ?? 0));
const notificationBadge = computed(() => countBadge(notifications?.unreadCount.value ?? 0));

function openSettings() {
  void router.push('/settings');
}
function openAccount() {
  void router.push('/settings/general/general');
}
function openChangelog() {
  void router.push('/changelog');
}
function handleLogout() {
  // Shell-owned sign-out: best-effort server-side revoke, then clear + reload
  // + redirect to login (see signOut()).
  void signOut();
}

/**
 * The active company at the top of the user menu, with a submenu of the
 * companies the account may switch to — grouped by tenant when there is more
 * than one. Picking one switches the session and reloads the shell.
 */
const companyMenuItem = computed<NavItem | null>(() => {
  const active = sessionScope.active.value;
  if (!active) return null;
  const activeID = active.company.id;
  const companyItems = (tenantID: number, companies: typeof active.tenant.companies): NavItem[] =>
    [...companies]
      .sort((a, b) => companyLabel(a).localeCompare(companyLabel(b)))
      .map((company) => ({
        id: `shell.user.company.${tenantID}.${company.id}`,
        text: companyLabel(company),
        icon: company.id === activeID ? Check : undefined,
        disabled: company.id === activeID || sessionScope.switching.value,
        action: () => void sessionScope.switchTo(tenantID, company.id),
      }));
  const tenants = sessionScope.tenants.value;
  let children: NavItem[] | undefined;
  if (sessionScope.canSwitch.value) {
    children =
      tenants.length > 1
        ? tenants.map((tenant) => ({
            id: `shell.user.tenant.${tenant.id}`,
            text: tenantLabel(tenant),
            icon: tenant.id === active.tenant.id ? Check : undefined,
            children: companyItems(tenant.id, tenant.companies),
          }))
        : companyItems(active.tenant.id, active.tenant.companies);
  }
  return {
    id: 'shell.user.company',
    text: t('connect.menu.company', { company: companyLabel(active.company) }),
    icon: Building2,
    // A single company is shown, not offered.
    disabled: !children,
    children,
  };
});

onMounted(() => {
  void sessionScope.load();
});

/**
 * Global shell controls at the far right: mentions, the notification bell
 * (its card list opens in the NavBar's panel), chat, apps and the avatar.
 */
const globalItems = computed<NavItem[]>(() => (inTile ? [] : [
  {
    id: 'shell.mentions',
    text: t('connect.mentions.title'),
    icon: AtSign,
    iconOnly: true,
    badge: mentionBadge.value,
    action: () => router.push('/mentions'),
  },
  {
    id: 'shell.notifications',
    text: t('connect.notifications.title'),
    icon: Bell,
    iconOnly: true,
    badge: notificationBadge.value,
    panel: ShellNotificationsPanel,
  },
  {
    id: 'shell.chat',
    text: t('connect.menu.chat'),
    icon: ChatIcon,
    iconOnly: true,
    disabled: !isAvailable('chat'),
    action: () => router.push('/chat'),
  },
  {
    id: 'shell.apps',
    text: t('connect.home.appsHeading'),
    icon: LayoutGrid,
    iconOnly: true,
    children: apps
      .filter((a) => isEntitled(a.id))
      .map((a) => ({
        id: `shell.app.${a.id}`,
        text: t(a.labelKey),
        icon: a.icon,
        // Greyed-out + non-clickable while the app's module isn't reachable.
        disabled: !isAvailable(a.moduleKey),
        action: () => goApp(a),
      })),
  },
  {
    id: 'shell.user',
    // Initials avatar + connection-status dot (green connected / red not).
    text: userName.value,
    icon: UserAvatar,
    iconOnly: true,
    children: [
      ...(companyMenuItem.value ? [companyMenuItem.value] : []),
      { id: 'shell.user.account', text: t('connect.menu.account'), icon: User, action: openAccount },
      {
        id: 'shell.user.downloads',
        text: t('connect.menu.downloads'),
        icon: Download,
        action: () => router.push('/downloads'),
      },
      {
        id: 'shell.user.formicaId',
        text: t('connect.menu.formicaId'),
        icon: Fingerprint,
        action: () => router.push('/formica_id'),
      },
      {
        id: 'shell.user.changelog',
        text: t('connect.menu.changelog'),
        icon: Sparkles,
        action: openChangelog,
      },
      { id: 'shell.user.settings', text: t('connect.menu.settings'), icon: Settings, action: openSettings },
      { id: 'shell.user.logout', text: t('connect.menu.logout'), icon: LogOut, action: handleLogout },
    ],
  },
]));

// Keep teleported app topbar tools clear of the global right cluster. The
// cluster width changes when badges appear or items register async on mount.
const navRootRef = ref<HTMLElement | null>(null);
let rightClusterObserver: ResizeObserver | null = null;

function syncRightClusterWidth() {
  const root = navRootRef.value;
  if (!root) return;
  const rightRow = root.querySelector<HTMLElement>('.menubar-root-align-right');
  const width = rightRow?.getBoundingClientRect().width ?? 0;
  root.style.setProperty('--cx-nav-right-cluster-width', `${Math.ceil(width) + 10}px`);
}

function attachRightClusterObserver() {
  rightClusterObserver?.disconnect();
  const root = navRootRef.value;
  if (!root) return;
  const rightRow = root.querySelector('.menubar-root-align-right');
  if (!rightRow) return;
  rightClusterObserver ??= new ResizeObserver(syncRightClusterWidth);
  rightClusterObserver.observe(rightRow);
  syncRightClusterWidth();
}

onMounted(() => {
  void nextTick(attachRightClusterObserver);
});

watch(
  () => activeApp.value?.id,
  () => void nextTick(attachRightClusterObserver),
);

onBeforeUnmount(() => {
  rightClusterObserver?.disconnect();
  rightClusterObserver = null;
});
</script>

<template>
  <div ref="navRootRef" class="cx-nav" :class="{ 'cx-nav--tile': noBarInTile }" data-nx-scope="navbar">
    <!-- Key on the active app so the bar remounts when the module changes: a
         fresh bar per app (focus, open menus, overflow). The NavBar follows
         the slots' order, so module items stay left of the globals and a
         reorder of the module's items (`reorderable`, I-3239) moves them
         in place without a remount. -->
    <NavBar
      v-if="!noBarInTile"
      :key="activeTileNav?.tileId ?? activeApp?.id ?? 'home'"
      theme="dark"
      :reorderable="reorderable && !activeTileNav"
      @icon-click="goHome"
      @reorder="onReorder"
    >
      <template #icon>
        <LogoFormicaDark />
      </template>

      <!-- Active module's left items, captioned from the module's own texts -->
      <template #leftAligned>
        <ShellNavItem v-for="item in leftItems" :key="item.id" :item="item" :app="activeApp?.id" />
      </template>

      <!-- Module right, then mentions, notifications, chat, apps and user -->
      <template #rightAligned>
        <ShellNavItem
          v-for="item in moduleRightItems"
          :key="item.id"
          :item="item"
          :app="activeApp?.id"
        />
        <ShellNavItem v-for="item in globalItems" :key="item.id" :item="item" />
      </template>
    </NavBar>

    <!-- Active app's topbar, teleported up into the navbar. Apps (mail/calendar/
         …) teleport their breadcrumb + tool buttons into `#<id>-topbar-*`; we
         render those targets here (their own layouts keep offscreen fallbacks).
         The --cn-* overrides recolor the (light-themed) tools for the dark bar —
         CSS custom properties inherit into teleported content. -->
    <div v-if="activeApp" class="cx-nav-app">
      <div :id="`${activeApp.id}-topbar-breadcrumb`" class="cx-nav-app-bc" />
      <div :id="`${activeApp.id}-topbar-tools`" class="cx-nav-app-tools" />
    </div>
  </div>
</template>
